import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isAdminHost } from '../config';

export type ThemeMode = 'dark' | 'light';

interface ThemeContextType {
  themeMode: ThemeMode;
  nextThemeMode: ThemeMode;
  setThemeMode: React.Dispatch<React.SetStateAction<ThemeMode>>;
  toggleThemeMode: () => void;
}

/* Only an explicit choice is stored, so the default can change later without
   every earlier visitor being pinned to whatever the default was. public/index.html
   reads the same key before first paint. */
export const THEME_STORAGE_KEY = 'imabird-theme';
export const DEFAULT_THEME_MODE: ThemeMode = 'dark';

const resolveInitialThemeMode = (): ThemeMode => {
  /* Ink is the default. A stored choice still wins, except on the admin
     console, which is always Paper. */
  if (typeof window === 'undefined') return DEFAULT_THEME_MODE;
  if (isAdminHost(window.location.hostname)) return 'light';
  try {
    const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (storedTheme === 'light' || storedTheme === 'dark') {
      return storedTheme;
    }
  } catch {
    // Ignore storage failures and keep the default.
  }
  return DEFAULT_THEME_MODE;
};

const storeThemeMode = (themeMode: ThemeMode) => {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, themeMode);
  } catch {
    // Ignore storage failures and continue with in-memory state.
  }
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(resolveInitialThemeMode);
  const nextThemeMode = themeMode === 'dark' ? 'light' : 'dark';

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeMode);
  }, [themeMode]);

  const setThemeMode = useCallback<React.Dispatch<React.SetStateAction<ThemeMode>>>((update) => {
    setThemeModeState((previousTheme) => {
      const chosenTheme = typeof update === 'function' ? update(previousTheme) : update;
      storeThemeMode(chosenTheme);
      return chosenTheme;
    });
  }, []);

  const value = useMemo<ThemeContextType>(
    () => ({
      themeMode,
      nextThemeMode,
      setThemeMode,
      toggleThemeMode: () => {
        setThemeMode((previousTheme) => (previousTheme === 'dark' ? 'light' : 'dark'));
      },
    }),
    [themeMode, nextThemeMode, setThemeMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
